import Mustache from "mustache"
import debounce from "lodash/debounce"
import { addEventListenerWithKey, runWhenIdle } from "shared/lib/utils.js"
import { onBFCacheLoad } from "shared/scripts/back_forward_cache_utils"
import { RecommendedServices, Service } from "./recommended_services"
import { RecentSearches } from "./recent_searches"
import { recommendationTemplate } from "./templates"
import { saveUserSearch, createSearchItemFromElement, SearchEntryInterface } from "./search_history"
import { SearchHelperNavigation } from "./search_helper_navigation"

export const UniversalSearchHeader = () => {
  const universalSearch = document.querySelector("#universal-search")
  if (!universalSearch) return

  // unhide the dropdown, hidden by default to avoid FOUC
  // unhide the search input, hidden by default on mobile
  universalSearch.parentElement?.removeAttribute("style")
  universalSearch.querySelector(".universal-search-helper")?.removeAttribute("style")

  const inputSearch = universalSearch.querySelector("input")
  const searchResultsContainer = universalSearch.querySelector(".search-preview > ul")

  RecentSearches()

  // Defer the recommended-services fetch until the browser is idle so it doesn't
  // compete with critical resources during initial load. As a bonus, short-lived
  // pages that redirect before idle (e.g. /unsubscribe opened from an email) never
  // fire it, avoiding a request that would only get aborted on navigation.
  let allServices: Service[] = []
  const servicesReady = new Promise<void>(resolve => {
    runWhenIdle(() => {
      // Settle on both outcomes. RecommendedServices swallows its own fetch errors
      // and resolves with [] today, but loadUserRecentSearches() runs before that
      // try/catch, so a throw there could reject. If servicesReady ever stayed
      // pending, getFuse() and every queued keystroke would hang forever.
      RecommendedServices().then(
        services => {
          allServices = services
          resolve()
        },
        () => resolve(),
      )
    })
  })

  SearchHelperNavigation(universalSearch)

  // Lazy-load Fuse.js (reduces initial bundle ~12KB) and only build the index once
  // the deferred services have arrived, so an early keystroke still searches the
  // full set rather than an empty index. Kick off the chunk download in parallel
  // with servicesReady rather than after it, so neither waits on the other.
  let fusePromise: Promise<{ search: (term: string) => Array<{ item: unknown }> }> | null = null
  const getFuse = () => {
    if (!fusePromise) {
      fusePromise = Promise.all([servicesReady, import("fuse.js")]).then(
        ([, { default: Fuse }]) => new Fuse(allServices, { includeScore: true, threshold: 0.3, keys: ["name"] }),
      )
    }
    return fusePromise
  }

  const triggerSearch = (event: Event) => {
    const keyboardEvent = event as KeyboardEvent
    const searchTerm = (keyboardEvent.target as HTMLInputElement).value.trim()

    if (!searchTerm) return

    if (keyboardEvent.key === "Enter") {
      window.App.Utils.logCustomDatadogAction("universal_search_trigger_via_enter", { term: searchTerm })

      const serviceMatch = allServices.find(
        service =>
          service.name.toLowerCase() === searchTerm.toLowerCase() ||
          service.module_name.toLowerCase() === searchTerm.toLowerCase()
      )

      if (serviceMatch) {
        // Exact match with a service name, navigate to the service page
        saveUserSearch({
          type: "service",
          term: searchTerm,
          details: {
            name: serviceMatch.name,
            brand_color: serviceMatch.brand_color,
            module_name: serviceMatch.module_name,
            image: serviceMatch.image,
          }
        })
        window.location.assign(`/${serviceMatch.module_name}`)
      } else {
        saveUserSearch({ type: "search", term: searchTerm })
        window.location.assign(`/search/query/${encodeURIComponent(searchTerm.toLowerCase())}`)
      }
    }
  }

  const handleSearchInput = (searchResultsContainer: Element) => async (event: Event) => {
    const searchTerm = (event.target as HTMLInputElement).value.trim()

    if (!searchTerm) return

    const fuse = await getFuse()
    const results = fuse.search(searchTerm).slice(0, 12)

    if (searchResultsContainer) {
      searchResultsContainer.innerHTML = ""
      results.forEach(result => {
        const rendered = Mustache.render(recommendationTemplate, result.item)
        searchResultsContainer.insertAdjacentHTML("beforeend", rendered)
      })
    }
  }

  const recommendedServiceClick = (event: Event) => {
    const serviceAnchor = (event.target as HTMLElement).closest("li > a") as HTMLAnchorElement | null

    const searchEntry = createSearchItemFromElement(serviceAnchor) as SearchEntryInterface | null
    if (!searchEntry) return
    saveUserSearch(searchEntry)
    if (inputSearch.value.trim()) {
      // Click on filtered recommended services
      window.App.Utils.logCustomDatadogAction(`universal_search_service_filter_clicked`, {
        service: serviceAnchor?.dataset?.term,
      })
    } else {
      // On explore, clicking on recent search that is a service, the result is a service search (same from recommended service).
      // On header, even on recent search, the service click will navigate to the service page.
      if (searchEntry.type === "search") {
        window.App.Utils.logCustomDatadogAction(`universal_search_recent_searches_clicked`, {
          term: serviceAnchor?.dataset?.term,
        })
      } else {
        window.App.Utils.logCustomDatadogAction(`universal_search_recommended_service_clicked`, {
          service: serviceAnchor?.dataset?.term,
        })
      }
    }
  }

  const debouncedHandleInput = debounce(handleSearchInput(searchResultsContainer), 100)
  addEventListenerWithKey(inputSearch, "input", "universal_search_header_input", debouncedHandleInput)
  addEventListenerWithKey(inputSearch, "keydown", "universal_search_header_keydown", triggerSearch)

  // Prefetch Fuse when main thread is idle to reduce first-type lag (review: cjanik).
  runWhenIdle(() => getFuse())

  const recentSearchesWrapper = universalSearch.querySelector(".recent-searches")
  if (recentSearchesWrapper) {
    addEventListenerWithKey(
      recentSearchesWrapper,
      "click",
      "universal_search_header_recent_click",
      recommendedServiceClick
    )
  }

  const recommendedServicesWrapper = universalSearch.querySelector(".recommended-services")
  if (recommendedServicesWrapper) {
    addEventListenerWithKey(
      recommendedServicesWrapper,
      "click",
      "universal_search_header_recommended_click",
      recommendedServiceClick
    )
  }

  const filteredServicesWrapper = universalSearch.querySelector(".search-preview")
  if (filteredServicesWrapper) {
    addEventListenerWithKey(
      filteredServicesWrapper,
      "click",
      "universal_search_header_filtered_click",
      recommendedServiceClick
    )
  }

  const searchNavigation = universalSearch.querySelector("a.search-navigation")
  if (searchNavigation) {
    addEventListenerWithKey(searchNavigation, "click", "universal_search_header_navigation_click", () => {
      window.App.Utils.logCustomDatadogAction("universal_search_trigger_via_navigation_link", {
        term: inputSearch?.value.trim(),
      })
    })
  }

  addEventListenerWithKey(inputSearch, "focus", "universal_search_header_focus", () => {
    window.App.Utils.logCustomDatadogAction("universal_search_click_search_bar")
  })

  const mobileIconSearch = document.querySelector("span.mobile-icon-search")
  if (mobileIconSearch) {
    addEventListenerWithKey(mobileIconSearch, "click", "universal_search_header_mobile_icon_click", () => {
      document.body.classList.add("mobile-universal-search-enabled")
      inputSearch?.focus()
      window.App.Utils.logCustomDatadogAction("mobile_universal_search_view")
    })
  }

  const mobileIconClose = document.querySelector(".mobile-close-search-icon")
  if (mobileIconClose) {
    addEventListenerWithKey(mobileIconClose, "click", "universal_search_header_mobile_close_click", () => {
      document.body.classList.remove("mobile-universal-search-enabled")
      inputSearch?.blur()
    })
  }

  addEventListenerWithKey(window, "scroll", "universal_search_header_scroll", () => {
    if (!document.body.classList.contains("mobile-universal-search-enabled")) return
    inputSearch?.blur()
  })

  onBFCacheLoad("universal_search_header", () => {
    if (inputSearch) {
      inputSearch.value = ""
      inputSearch.dispatchEvent(new Event("input", { bubbles: true }))
    }
    if (searchResultsContainer) searchResultsContainer.innerHTML = ""

    UniversalSearchHeader()
  })
}
